import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    Stack,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {useNavigate} from 'react-router';
import LeftPanelTabs from '../../products/components/common/LeftPanelTabs';
import LinkedEeiCardsSection from './LinkedEeiCardsSection';
import SourceProvenanceChip from '../../../components/segmentItem/SourceProvenanceChip';
import {PROVENANCE_KIND} from '../../products/reportEditorPage/leftPanel/components/sourcesProvenance';
import useSourcesFileViewerStore from '../../../stores/useSourcesFileViewerStore';
import {getCollectionTask, listCollectionMaterials} from '../collectionTaskApi';
import BasicRichTextEditor from './BasicRichTextEditor';
import PersonCell, {indexUsersById} from './PersonCell';
import {getUsersByIds} from '../../../firebase/firestore/users';

const COLLECTED_PROVENANCE = {
    kind: PROVENANCE_KIND.myFile,
    primary: 'Collected Data',
};

function MaterialCard({material, selected, onOpen, authorProfile}) {
    const title = material.title && material.title !== material.cmNumber
        ? material.title
        : (material.cmNumber || 'Untitled material');
    const updated = material.updatedAt ? new Date(material.updatedAt).toLocaleString() : null;
    const submitted = material.status === 'submitted';
    const rawPreview = material.previewText || '';
    const previewText = /^start writing\.?\.?\.?$/i.test(rawPreview.trim()) ? '' : rawPreview;
    const previewImageUrl = material.previewImageUrl || null;

    return (
        <Stack
            bgcolor={selected ? '#eeeeff' : 'white'}
            borderRadius={2}
            outline={selected ? '2px dotted blue' : '1px solid #cde'}
            overflow="hidden"
            flexShrink={0}
            spacing={1}
            p={1.5}
            onClick={() => onOpen(material)}
            sx={{
                cursor: 'pointer',
                transition: 'box-shadow 0.2s ease',
                '&:hover': {
                    zIndex: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                },
            }}
        >
            {previewImageUrl && (
                <Box
                    component="img"
                    src={previewImageUrl}
                    alt=""
                    sx={{
                        width: '100%',
                        maxHeight: 120,
                        objectFit: 'cover',
                        borderRadius: 1,
                        bgcolor: 'action.hover',
                    }}
                />
            )}
            <Typography
                variant="body2"
                sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    // Never inherit NER/EL underline styles into card previews.
                    '& .wikidata-entity, & .wikidata-entity--mention, & .wikidata-entity--linked': {
                        color: 'inherit !important',
                        background: 'transparent !important',
                        borderBottom: 'none !important',
                        textDecoration: 'none !important',
                    },
                }}
            >
                {previewText || title}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <SourceProvenanceChip provenance={COLLECTED_PROVENANCE}/>
                {material.collectionMethod && (
                    <Chip size="small" label={material.collectionMethod} variant="outlined"/>
                )}
                <Chip
                    size="small"
                    label={submitted ? 'Submitted' : 'Draft'}
                    color={submitted ? 'success' : 'default'}
                    variant="outlined"
                />
                {material.cmNumber && (
                    <Typography variant="caption" color="text.secondary" sx={{fontFamily: 'monospace'}}>
                        {material.cmNumber}
                    </Typography>
                )}
            </Stack>
            {material.createdBy && (
                <Box onClick={(e) => e.stopPropagation()}>
                    <PersonCell userId={material.createdBy} profile={authorProfile}/>
                </Box>
            )}
            {updated && (
                <Typography variant="caption" color="text.secondary">
                    Updated {updated}
                </Typography>
            )}
        </Stack>
    );
}

function MaterialsList({ctNumber, materials, loading, error, selectedId, onOpen, onRefresh, people}) {
    return (
        <Stack spacing={0} sx={{flex: 1, minHeight: 0, overflow: 'hidden'}}>
            <Stack px={1.5} py={1} spacing={0.5} borderBottom="1px solid" borderColor="divider">
                <Typography variant="subtitle2">
                    {ctNumber ? `${ctNumber} materials` : 'Collected materials'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    Read-only view of collected text and images while drafting the IIR.
                </Typography>
                <Button size="small" onClick={onRefresh} sx={{alignSelf: 'flex-start', mt: 0.5}}>
                    Refresh
                </Button>
            </Stack>
            <Stack spacing={1} p={1.5} sx={{flex: 1, overflowY: 'auto', minHeight: 0}}>
                {loading && (
                    <Stack alignItems="center" py={4}><CircularProgress size={24}/></Stack>
                )}
                {error && (
                    <Typography color="error" variant="body2">{error}</Typography>
                )}
                {!loading && !error && materials.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        No materials attached to this Collection Task yet.
                    </Typography>
                )}
                {materials.map((m) => (
                    <MaterialCard
                        key={m.id}
                        material={m}
                        selected={selectedId === m.id}
                        onOpen={onOpen}
                        authorProfile={people?.[m.createdBy]}
                    />
                ))}
            </Stack>
        </Stack>
    );
}

/** Legacy rich-text CM viewer (report-backed only). New CMs open via Sources file viewer. */
function LegacyMaterialViewer({material, onBack}) {
    const navigate = useNavigate();
    const header = (
        <Box px={1.5} py={1} borderBottom="1px solid #CECFD2">
            <Stack direction="row" alignItems="center" spacing={0.5}>
                <IconButton size="small" onClick={onBack} aria-label="Back to materials list">
                    <ArrowBackIcon fontSize="small"/>
                </IconButton>
                <Box minWidth={0} flex={1}>
                    <Typography variant="caption" color="text.secondary" sx={{fontFamily: 'monospace'}}>
                        {material.cmNumber || material.id}
                    </Typography>
                    <Typography variant="body2" noWrap>
                        {material.title && material.title !== material.cmNumber
                            ? material.title
                            : 'Collection material'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {[material.collectionMethod, 'Read-only'].filter(Boolean).join(' · ')}
                    </Typography>
                </Box>
                <IconButton
                    size="small"
                    aria-label="Open material in full editor"
                    onClick={() => navigate(`/collection/data/${material.id}/edit`)}
                >
                    <OpenInNewIcon fontSize="small"/>
                </IconButton>
            </Stack>
        </Box>
    );

    return (
        <Box sx={{flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column'}}>
            <BasicRichTextEditor
                documentId={material.reportId}
                documentHeader={header}
                showDocumentHeader={true}
                showToolbar={false}
                readOnly={true}
            />
        </Box>
    );
}

function EeiList({linkedEeis, ctNumber}) {
    return (
        <Stack spacing={0} sx={{flex: 1, minHeight: 0, overflow: 'hidden'}}>
            <Stack px={1.5} py={1} spacing={0.5} borderBottom="1px solid" borderColor="divider">
                <Typography variant="subtitle2">
                    {ctNumber ? `${ctNumber} EEIs` : 'Linked EEIs'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    Essential Elements of Information linked to this Collection Task.
                </Typography>
            </Stack>
            {linkedEeis.length === 0 ? (
                <Stack p={1.5}>
                    <Typography variant="body2" color="text.secondary">
                        No EEIs linked to this Collection Task.
                    </Typography>
                </Stack>
            ) : (
                <LinkedEeiCardsSection
                    linkedEeis={linkedEeis}
                    sx={{flex: 1, overflowY: 'auto', minHeight: 0, p: 1.5}}
                />
            )}
        </Stack>
    );
}

/**
 * FINTEL-style left panel for IIR: Data and EEI tabs for related Collection Task
 * materials and linked EEIs (read-only).
 */
export default function IirMaterialsPanel({taskId}) {
    const navigate = useNavigate();
    const openViewer = useSourcesFileViewerStore((s) => s.open);
    const [tab, setTab] = useState('data');
    const [materials, setMaterials] = useState([]);
    const [people, setPeople] = useState({});
    const [ctNumber, setCtNumber] = useState(null);
    const [linkedEeis, setLinkedEeis] = useState([]);
    const [loading, setLoading] = useState(Boolean(taskId));
    const [error, setError] = useState(null);
    const [selected, setSelected] = useState(null);

    const handleOpenMaterial = useCallback((material) => {
        if (material?.dataItemId) {
            openViewer({
                fileId: String(material.dataItemId),
                canonicalFileId: String(material.dataItemId),
                fileName: material.title || material.cmNumber || 'Collected Material',
                fileType: material.mimeType || 'unknown',
                fileSource: 'collection_material',
            });
            return;
        }
        if (material?.reportId) {
            setSelected(material);
            return;
        }
        navigate(`/collection/data/${material.id}/edit`);
    }, [navigate, openViewer]);

    const reload = useCallback(async () => {
        if (!taskId) {
            setMaterials([]);
            setPeople({});
            setCtNumber(null);
            setLinkedEeis([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const [list, task] = await Promise.all([
                listCollectionMaterials({taskId}),
                getCollectionTask(taskId).catch(() => null),
            ]);
            const materialsList = Array.isArray(list) ? list : [];
            setMaterials(materialsList);
            setCtNumber(task?.ctNumber || null);
            const taskEeis = Array.isArray(task?.eeis) ? task.eeis : [];
            setLinkedEeis(taskEeis);
            const authorIds = [...new Set(materialsList.map((m) => m.createdBy).filter(Boolean))];
            if (authorIds.length) {
                setPeople(indexUsersById(await getUsersByIds(authorIds)));
            } else {
                setPeople({});
            }
        } catch (e) {
            setError(e?.message || 'Failed to load materials');
            setMaterials([]);
            setPeople({});
            setLinkedEeis([]);
        } finally {
            setLoading(false);
        }
    }, [taskId]);

    useEffect(() => {
        reload();
    }, [reload]);

    useEffect(() => {
        setSelected(null);
    }, [taskId]);

    const dataContent = useMemo(() => {
        if (!taskId) {
            return (
                <Stack p={2}>
                    <Typography variant="body2" color="text.secondary">
                        This IIR is not linked to a Collection Task, so there are no collected materials to show.
                    </Typography>
                </Stack>
            );
        }
        if (selected?.reportId && !selected?.dataItemId) {
            return (
                <LegacyMaterialViewer
                    material={selected}
                    onBack={() => setSelected(null)}
                />
            );
        }
        return (
            <MaterialsList
                ctNumber={ctNumber}
                materials={materials}
                people={people}
                loading={loading}
                error={error}
                selectedId={selected?.id}
                onOpen={handleOpenMaterial}
                onRefresh={reload}
            />
        );
    }, [taskId, selected, ctNumber, materials, people, loading, error, reload, handleOpenMaterial]);

    const eeiContent = useMemo(() => (
        <EeiList linkedEeis={linkedEeis} ctNumber={ctNumber}/>
    ), [linkedEeis, ctNumber]);

    const tabs = useMemo(() => [
        {value: 'data', label: 'Data', content: dataContent},
        {value: 'eei', label: 'EEI', content: eeiContent},
    ], [dataContent, eeiContent]);

    return (
        <Stack height="100%" bgcolor="white" sx={{minHeight: 0, overflow: 'hidden'}}>
            <LeftPanelTabs
                tabs={tabs}
                activeTab={tab}
                onChange={(_, value) => setTab(value)}
                ariaLabel="IIR reference panel"
            />
        </Stack>
    );
}
